import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Button,
  Icon,
  AspectRatio,
} from "@chakra-ui/react";
import {
  FaArrowRight,
  FaPlayCircle,
  FaPiggyBank,
  FaChartLine,
  FaHome,
  FaUniversity,
  FaSyncAlt,
  FaCheckCircle,
  FaCoins,
} from "react-icons/fa";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { useNavigate } from "react-router-dom";

const PROPERTY_PRIMARY = "#0D7F6E";
const NAVY = "#141C49";
const NAVY_MID = "#1C2A5E";
const GOLD = "#C9A24B";

// Illustrative 10-year comparison: $15,000/year directed into a leveraged
// investment property vs. the same $15,000/year placed into cash savings.
const EQUITY_VS_SAVINGS = [
  { year: "Yr 0", propertyEquity: 120000, cashSavings: 0 },
  { year: "Yr 1", propertyEquity: 165000, cashSavings: 15000 },
  { year: "Yr 2", propertyEquity: 212160, cashSavings: 30600 },
  { year: "Yr 3", propertyEquity: 261610, cashSavings: 46824 },
  { year: "Yr 4", propertyEquity: 313486, cashSavings: 63697 },
  { year: "Yr 5", propertyEquity: 367935, cashSavings: 81245 },
  { year: "Yr 6", propertyEquity: 425111, cashSavings: 99495 },
  { year: "Yr 7", propertyEquity: 485178, cashSavings: 118474 },
  { year: "Yr 8", propertyEquity: 548309, cashSavings: 138213 },
  { year: "Yr 9", propertyEquity: 614687, cashSavings: 158742 },
  { year: "Yr 10", propertyEquity: 684509, cashSavings: 180092 },
];

const EQUITY_GROWTH = 684509 - 120000; // ~$564,509
const CASH_RESULT = 180092;

// Illustrative, approximate historical median house price trends.
// Indicative only - rounded for illustration based on long-run public
// trend data; see references listed beneath the chart.
const REGIONAL_QLD_PRICES = [
  { year: "1970", brisbane: 18000, sunshineCoast: 14000, toowoomba: 10000, cairns: 11000, townsville: 10500 },
  { year: "1980", brisbane: 45000, sunshineCoast: 38000, toowoomba: 26000, cairns: 30000, townsville: 28000 },
  { year: "1990", brisbane: 105000, sunshineCoast: 90000, toowoomba: 65000, cairns: 75000, townsville: 70000 },
  { year: "1995", brisbane: 138000, sunshineCoast: 125000, toowoomba: 85000, cairns: 100000, townsville: 95000 },
  { year: "2000", brisbane: 175000, sunshineCoast: 165000, toowoomba: 115000, cairns: 135000, townsville: 125000 },
  { year: "2005", brisbane: 350000, sunshineCoast: 380000, toowoomba: 220000, cairns: 270000, townsville: 240000 },
  { year: "2010", brisbane: 450000, sunshineCoast: 430000, toowoomba: 320000, cairns: 360000, townsville: 330000 },
  { year: "2015", brisbane: 480000, sunshineCoast: 470000, toowoomba: 350000, cairns: 380000, townsville: 340000 },
  { year: "2020", brisbane: 575000, sunshineCoast: 580000, toowoomba: 390000, cairns: 420000, townsville: 350000 },
  { year: "2024", brisbane: 900000, sunshineCoast: 950000, toowoomba: 580000, cairns: 600000, townsville: 470000 },
];

const PRICE_SOURCES = [
  "CoreLogic, Hedonic Home Value Index & long-run regional market reports",
  "Real Estate Institute of Queensland (REIQ), Quarterly Market Monitor",
  "Australian Bureau of Statistics (ABS), Residential Property Price Indexes",
  "Queensland Government Statistician's Office, median house sale prices by region",
];

const currency = (value: number) =>
  `$${value.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;

const TAKEAWAYS = [
  {
    icon: FaHome,
    title: "Pay Off Your Home Faster",
    desc: "Use equity growth from an investment property to accelerate repayments on your own mortgage.",
  },
  {
    icon: FaUniversity,
    title: "Minimise Your Tax",
    desc: "Structure borrowing, depreciation, and deductions correctly so more of your income works for you.",
  },
  {
    icon: FaSyncAlt,
    title: "Compound Into Retirement",
    desc: "Redraw equity to acquire a second property, doubling your exposure to growth and cashflow.",
  },
];

const STRATEGY_STEPS = [
  {
    icon: FaPiggyBank,
    title: "1. Direct $10K-$20K a Year",
    desc: "Rather than parking surplus income in a savings account, it funds the holding costs and buffer on a leveraged investment property.",
  },
  {
    icon: FaChartLine,
    title: "2. Let Leverage Build Equity",
    desc: "Because the loan is secured against the full property value, capital growth applies to the whole asset - not just the cash you put in.",
  },
  {
    icon: FaCoins,
    title: "3. Access the Equity",
    desc: "As equity builds, it can be redrawn to pay down your own home loan faster or fund a deposit on a second property.",
  },
  {
    icon: FaSyncAlt,
    title: "4. Double Down & Compound",
    desc: "A second property repeats the cycle - more growth, more cashflow, and a larger asset base heading into retirement.",
  },
];

const PropertyWebinarMortgageTaxRetirement: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/resources/webinar-mortgage-tax-retirement"
        fallbackTitle="Paying Off Your Mortgage Faster, Minimise Your Tax and Retirement Planning"
        fallbackDescription="Watch our webinar on using investment property equity to pay off your mortgage sooner, minimise tax, and build retirement wealth - benchmarked against historical Brisbane and regional QLD property growth."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box bg={NAVY} color="white" py={{ base: 14, md: 20 }}>
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center">
            <Badge
              bg="transparent"
              color={PROPERTY_PRIMARY}
              border="1px solid"
              borderColor="rgba(13, 127, 110, 0.4)"
              px={4}
              py={1.5}
              borderRadius="full"
              fontSize="xs"
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="0.15em"
            >
              <HStack spacing={2}>
                <Icon as={FaPlayCircle} />
                <Text>Free Webinar</Text>
              </HStack>
            </Badge>
            <Heading size="2xl" fontWeight="black" maxW="3xl" lineHeight="1.2">
              Paying Off Your Mortgage Faster, Minimise Your Tax and{" "}
              <Text as="span" color={PROPERTY_PRIMARY} fontStyle="italic">
                Retirement Planning
              </Text>
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl" lineHeight="1.8">
              How a small annual contribution into the right investment
              property has historically created hundreds of thousands of
              dollars in equity - and how that equity can pay off your home
              sooner, cut your tax bill, and fund your retirement.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Video */}
      <Container maxW="container.lg" mt={{ base: -8, md: -12 }} mb={{ base: 12, md: 16 }} position="relative" zIndex={2}>
        <Box
          bg="white"
          borderRadius="2xl"
          boxShadow="2xl"
          p={{ base: 2, md: 3 }}
          border="1px solid"
          borderColor="gray.100"
        >
          <AspectRatio ratio={16 / 9} borderRadius="xl" overflow="hidden">
            <iframe
              src="https://app.heygen.com/embeds/26233c64696944ccba4e2db0cd9d5d0b"
              title="Version 4 Webinar"
              allow="encrypted-media; fullscreen;"
              allowFullScreen
            />
          </AspectRatio>
        </Box>
      </Container>

      {/* Takeaways */}
      <Container maxW="container.xl" pb={{ base: 12, md: 16 }}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {TAKEAWAYS.map((item) => (
            <HStack
              key={item.title}
              align="start"
              spacing={4}
              bg="gray.50"
              p={6}
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.100"
            >
              <Icon as={item.icon} boxSize={7} color={PROPERTY_PRIMARY} mt={1} />
              <VStack align="start" spacing={1}>
                <Heading size="sm" color={NAVY}>
                  {item.title}
                </Heading>
                <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                  {item.desc}
                </Text>
              </VStack>
            </HStack>
          ))}
        </SimpleGrid>
      </Container>

      {/* Equity vs Saving comparison */}
      <Box bg="gray.50" py={{ base: 14, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Badge colorScheme="green" px={3} py={1} borderRadius="full">
              The Core Idea
            </Badge>
            <Heading size="xl" color={NAVY} maxW="3xl">
              $15,000 a Year Into Property vs. $15,000 a Year Into Savings
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl" lineHeight="1.8">
              Directing roughly $10,000-$20,000 a year toward an investment
              property's holding costs has historically built equity far
              faster than saving the same amount in cash - because growth
              compounds on the full value of the asset, not just the money
              you put in.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={10} alignItems="center" mb={10}>
            <Box bg="white" p={6} borderRadius="xl" border="1px solid" borderColor="gray.200" boxShadow="sm">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={EQUITY_VS_SAVINGS} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => `$${Math.round(value / 1000)}k`}
                  />
                  <Tooltip formatter={(value: number) => currency(value)} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="propertyEquity"
                    name="Property Equity"
                    stroke={PROPERTY_PRIMARY}
                    strokeWidth={3}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="cashSavings"
                    name="Cash Savings"
                    stroke={GOLD}
                    strokeWidth={3}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
              <Text fontSize="xs" color="gray.500" mt={3}>
                Illustrative example only: $600,000 leveraged property at 6%
                p.a. capital growth with gradual loan reduction, versus
                $15,000/year saved at 4% p.a. Not a forecast or guarantee.
              </Text>
            </Box>

            <VStack align="stretch" spacing={5}>
              <Box bg={NAVY} color="white" p={6} borderRadius="xl">
                <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="whiteAlpha.700">
                  Equity Growth Over 10 Years
                </Text>
                <Heading size="2xl" color={PROPERTY_PRIMARY} mt={2}>
                  {currency(EQUITY_GROWTH)}
                </Heading>
                <Text fontSize="sm" color="whiteAlpha.800" mt={2}>
                  Built from leveraged capital growth on a single investment
                  property, funded by ~$15,000/year in holding costs.
                </Text>
              </Box>
              <Box bg="white" border="2px solid" borderColor="gray.200" p={6} borderRadius="xl">
                <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500">
                  Same $15,000/Year, Saved in Cash
                </Text>
                <Heading size="2xl" color="gray.700" mt={2}>
                  {currency(CASH_RESULT)}
                </Heading>
                <Text fontSize="sm" color="gray.600" mt={2}>
                  No leverage, no asset growth working in the background -
                  just contributions plus interest.
                </Text>
              </Box>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Historical QLD chart */}
      <Box bg="white" py={{ base: 14, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Badge colorScheme="purple" px={3} py={1} borderRadius="full">
              The Evidence
            </Badge>
            <Heading size="xl" color={NAVY} maxW="3xl">
              Half a Century of Growth Across Brisbane &amp; Regional QLD
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl" lineHeight="1.8">
              Property cycles move differently across markets, but going back
              to the 1970s, the long-run trend across Brisbane and
              Queensland's regional centres has been sustained capital growth
              over multi-decade periods.
            </Text>
          </VStack>

          <Box bg="gray.50" p={{ base: 4, md: 8 }} borderRadius="xl" border="1px solid" borderColor="gray.100">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={REGIONAL_QLD_PRICES} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `$${Math.round(value / 1000)}k`}
                />
                <Tooltip formatter={(value: number) => currency(value)} />
                <Legend />
                <Line type="monotone" dataKey="brisbane" name="Brisbane" stroke={NAVY} strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="sunshineCoast" name="Sunshine Coast" stroke={PROPERTY_PRIMARY} strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="toowoomba" name="Toowoomba" stroke={GOLD} strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="cairns" name="Cairns" stroke="#8854D0" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="townsville" name="Townsville" stroke="#E53E3E" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
            <Text fontSize="xs" color="gray.500" mt={4}>
              Approximate median house prices shown for illustration only,
              rounded to indicate long-run trend direction. Figures are not
              sourced from a single dataset, do not account for dwelling mix
              or quality changes over time, and growth has not been linear -
              individual years included significant volatility, flat
              periods, and localised downturns (e.g. mining-town corrections
              in North Queensland). Earlier decades (1970s-1980s) are
              estimated with lower precision than recent years. Past
              performance is not a reliable indicator of future performance.
            </Text>
            <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
              <Text fontSize="xs" fontWeight="600" color="gray.600" mb={2}>
                References:
              </Text>
              <VStack align="start" spacing={1}>
                {PRICE_SOURCES.map((source) => (
                  <Text key={source} fontSize="xs" color="gray.500">
                    - {source}
                  </Text>
                ))}
              </VStack>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Strategy steps */}
      <Box bg={NAVY_MID} color="white" py={{ base: 14, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Heading size="xl" maxW="3xl" mx="auto">
              The Double-Down Strategy
            </Heading>
            <Text fontSize="lg" color="gray.300" maxW="2xl" mx="auto" lineHeight="1.8">
              The same equity that pays off your home faster can also fund
              your next acquisition - compounding your growth and cashflow
              for retirement.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {STRATEGY_STEPS.map((step) => (
              <VStack
                key={step.title}
                align="start"
                spacing={3}
                bg="whiteAlpha.100"
                p={6}
                borderRadius="xl"
                border="1px solid"
                borderColor="whiteAlpha.200"
              >
                <Icon as={step.icon} boxSize={7} color={PROPERTY_PRIMARY} />
                <Heading size="sm">{step.title}</Heading>
                <Text fontSize="sm" color="gray.300" lineHeight="1.6">
                  {step.desc}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Box bg="white" py={{ base: 16, md: 20 }} textAlign="center">
        <Container maxW="container.md">
          <Heading size="2xl" fontWeight="bold" color={NAVY} mb={4}>
            See What This Strategy Could Look Like For You
          </Heading>
          <Text color="gray.600" fontSize="lg" lineHeight="1.7" maxW="480px" mx="auto" mb={8}>
            Book a free consultation and we'll walk through what an equity
            strategy could mean for your mortgage, your tax position, and
            your retirement timeline.
          </Text>
          <HStack spacing={4} justify="center" flexWrap="wrap">
            <Button
              size="lg"
              bg={PROPERTY_PRIMARY}
              color="white"
              _hover={{ bg: "#0a6459" }}
              rightIcon={<FaArrowRight />}
              px={8}
              onClick={() => navigate("/property/consultation")}
            >
              Book Free Consultation
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor={NAVY}
              color={NAVY}
              _hover={{ bg: NAVY, color: "white" }}
              px={8}
              onClick={() => navigate("/property/case-studies")}
            >
              See Real Results
            </Button>
          </HStack>
          <HStack justify="center" mt={6} color="gray.500" fontSize="sm" spacing={2}>
            <Icon as={FaCheckCircle} color={PROPERTY_PRIMARY} />
            <Text>No cost. No obligation. Just a clear plan.</Text>
          </HStack>
        </Container>
      </Box>

      {/* Disclaimer */}
      <Container maxW="container.lg" pb={10}>
        <Box pt={6} borderTop="1px solid" borderColor="gray.200">
          <Text fontSize="xs" color="gray.500" lineHeight="1.7">
            <Text as="span" fontWeight="bold">
              Important:
            </Text>{" "}
            This webinar and page are provided for general information and
            illustrative purposes only and do not constitute financial, tax,
            or investment advice. The equity growth, savings, and historical
            price figures shown are simplified examples used to demonstrate
            a concept - they are not projections, forecasts, or guarantees of
            future performance, and do not account for your individual
            circumstances, borrowing capacity, interest rate movements, or
            property-specific factors. Property investment involves risk,
            including the risk of capital loss. You should seek independent
            financial, tax, and legal advice before making any investment
            decisions. One Group Australia's advisers are not licensed
            financial advisers under the Corporations Act 2001 (Cth).
          </Text>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyWebinarMortgageTaxRetirement;
